import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Calculator, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TaxCalculator() {
  const [formData, setFormData] = useState({
    total_income: "",
    total_deductions: "",
    filing_status: "single",
    state: "CA",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoLoading, setAutoLoading] = useState(true);

  useEffect(() => {
    loadAutoData();
  }, []);

  const loadAutoData = async () => {
    try {
      setAutoLoading(true);
      const [incomeRes, deductionsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/income`),
        axios.get(`${API}/deductions`),
        axios.get(`${API}/tax-settings`),
      ]);

      const totalIncome = incomeRes.data.reduce((sum, item) => sum + item.amount, 0);
      const totalDeductions = deductionsRes.data.total_claimed || 0;

      setFormData({
        total_income: totalIncome.toString(),
        total_deductions: totalDeductions.toString(),
        filing_status: settingsRes.data.filing_status,
        state: settingsRes.data.state,
      });
    } catch (error) {
      console.error("Error loading auto data:", error);
    } finally {
      setAutoLoading(false);
    }
  };

  const calculateTax = async (e) => {
    e.preventDefault();
    if (!formData.total_income) {
      toast.error("Please enter your total income");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/tax-calculation`, {
        total_income: parseFloat(formData.total_income),
        total_deductions: parseFloat(formData.total_deductions || 0),
        filing_status: formData.filing_status,
        state: formData.state,
      });
      setResult(response.data);
      toast.success("Tax calculation complete");
    } catch (error) {
      console.error("Error calculating tax:", error);
      toast.error("Failed to calculate tax");
    } finally {
      setLoading(false);
    }
  };

  const chartData = result
    ? [
        { name: "Federal Tax", value: result.federal_tax },
        { name: "State Tax", value: result.state_tax },
        { name: "Take Home", value: parseFloat(formData.total_income) - result.total_tax },
      ]
    : [];

  const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-4))"];

  if (autoLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-4xl sm:text-5xl font-heading font-light tracking-tight" data-testid="tax-calculator-title">
          Tax Calculator
        </h1>
        <p className="text-lg text-muted-foreground mt-2">Estimate your tax liability</p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Calculator Form */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading font-light">Calculate Your Taxes</CardTitle>
            <CardDescription>Enter your financial information</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={calculateTax} className="space-y-6">
              <div>
                <Label htmlFor="income">Total Income</Label>
                <Input
                  id="income"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.total_income}
                  onChange={(e) => setFormData({ ...formData, total_income: e.target.value })}
                  data-testid="income-input"
                />
              </div>
              <div>
                <Label htmlFor="deductions">Total Deductions</Label>
                <Input
                  id="deductions"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.total_deductions}
                  onChange={(e) => setFormData({ ...formData, total_deductions: e.target.value })}
                  data-testid="deductions-input"
                />
              </div>
              <div>
                <Label htmlFor="filing-status">Filing Status</Label>
                <Select
                  value={formData.filing_status}
                  onValueChange={(value) => setFormData({ ...formData, filing_status: value })}
                >
                  <SelectTrigger data-testid="filing-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="married">Married Filing Jointly</SelectItem>
                    <SelectItem value="head">Head of Household</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Select value={formData.state} onValueChange={(value) => setFormData({ ...formData, state: value })}>
                  <SelectTrigger data-testid="state-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CA">California</SelectItem>
                    <SelectItem value="NY">New York</SelectItem>
                    <SelectItem value="TX">Texas</SelectItem>
                    <SelectItem value="FL">Florida</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="calculate-tax-button">
                {loading ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Calculating...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate Tax
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <Card>
              <CardHeader>
                <CardTitle className="font-heading font-light">Tax Breakdown</CardTitle>
                <CardDescription>Estimated tax liability for 2024</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 bg-secondary/50 rounded-lg">
                    <span className="text-sm font-medium">Taxable Income</span>
                    <span className="text-lg font-mono font-semibold" data-testid="taxable-income-result">
                      ${result.taxable_income.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-secondary/50 rounded-lg">
                    <span className="text-sm font-medium">Federal Tax</span>
                    <span className="text-lg font-mono font-semibold text-primary" data-testid="federal-tax-result">
                      ${result.federal_tax.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-secondary/50 rounded-lg">
                    <span className="text-sm font-medium">State Tax</span>
                    <span className="text-lg font-mono font-semibold text-primary" data-testid="state-tax-result">
                      ${result.state_tax.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-6 bg-accent text-accent-foreground rounded-lg">
                    <span className="font-medium">Total Tax</span>
                    <span className="text-2xl font-mono font-bold" data-testid="total-tax-result">
                      ${result.total_tax.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-primary/10 rounded-lg">
                    <span className="text-sm font-medium flex items-center">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Effective Rate
                    </span>
                    <span className="text-lg font-mono font-semibold" data-testid="effective-rate-result">
                      {result.effective_rate}%
                    </span>
                  </div>
                </div>

                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {!result && (
          <Card>
            <CardContent className="p-12 text-center">
              <Calculator className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">Ready to calculate</h3>
              <p className="text-muted-foreground">Fill in the form and click Calculate Tax to see your results</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
